import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, LogIn, UserPlus, Star, ArrowLeft, Upload, CheckCircle2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pms`;

const inp = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40";
const btn = "w-full flex items-center justify-center gap-2 rounded-full font-semibold px-5 py-3 text-sm transition-colors";

export default function SelfCheckin() {
  const { pid, token } = useParams();
  const [landing, setLanding] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("home"); // home | checkin | visitor | done

  useEffect(() => {
    axios.get(`${API}/public/checkin/${pid}/${token}`)
      .then(({ data }) => setLanding(data))
      .catch((e) => setErr(e.response?.data?.detail || "Invalid or expired QR link"));
  }, [pid, token]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-red-200 p-8 text-center">
          <div className="text-red-600 mb-3"><X size={44} className="mx-auto" /></div>
          <h1 className="font-bold text-lg text-slate-900">Link unavailable</h1>
          <p className="text-sm text-slate-600 mt-2">{err}</p>
          <p className="text-xs text-slate-400 mt-4">Please contact the property front desk.</p>
        </div>
      </div>
    );
  }
  if (!landing) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={30} /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-md mx-auto px-5 py-8">
        <header className="text-center mb-6">
          {landing.logo_url && <img src={landing.logo_url} alt="logo" className="h-16 w-16 mx-auto object-contain rounded-xl bg-white border border-slate-200 p-2" />}
          <h1 className="mt-3 font-bold text-slate-900 text-xl">{landing.property_name}</h1>
          {landing.city && <p className="text-xs text-slate-500 mt-0.5">{landing.city}</p>}
        </header>

        {mode === "home" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600 text-center mb-4">Welcome! How can we help?</p>
            <button data-testid="qr-start-checkin" onClick={() => setMode("checkin")}
              className={`${btn} bg-[#4F46E5] hover:bg-[#4338CA] text-white shadow-sm`}>
              <LogIn size={16} /> Self Check-in
            </button>
            <button data-testid="qr-start-visitor" onClick={() => setMode("visitor")}
              className={`${btn} bg-white border border-slate-300 text-slate-800 hover:border-slate-500`}>
              <UserPlus size={16} /> Visitor Pass
            </button>
            {landing.google_review_url && (
              <a data-testid="qr-google-review" href={landing.google_review_url} target="_blank" rel="noreferrer"
                className={`${btn} bg-amber-500 hover:bg-amber-600 text-white`}>
                <Star size={16} /> Leave a Google Review
              </a>
            )}
            <p className="text-[11px] text-slate-400 text-center mt-6">
              Powered by Atiti PMS · Your data is encrypted and only visible to {landing.property_name}.
            </p>
          </div>
        )}

        {mode === "checkin" && (
          <SelfCheckinFlow pid={pid} token={token} onBack={() => setMode("home")} onDone={() => setMode("done")} />
        )}
        {mode === "visitor" && (
          <VisitorPassFlow pid={pid} token={token} onBack={() => setMode("home")} onDone={() => setMode("done")} />
        )}
        {mode === "done" && (
          <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center">
            <CheckCircle2 size={44} className="text-emerald-500 mx-auto mb-3" />
            <h2 className="font-bold text-slate-900">All done!</h2>
            <p className="text-sm text-slate-600 mt-2">The front desk has been notified.</p>
            <button onClick={() => setMode("home")} className="mt-6 text-[#4F46E5] text-sm font-semibold">Back to home</button>
          </div>
        )}
      </div>
    </div>
  );
}

function BackBtn({ onClick }) {
  return (
    <button onClick={onClick} className="text-slate-500 hover:text-slate-800 text-xs font-semibold flex items-center gap-1 mb-3">
      <ArrowLeft size={13} /> Back
    </button>
  );
}

function SelfCheckinFlow({ pid, token, onBack, onDone }) {
  const [step, setStep] = useState("lookup"); // lookup | form
  const [lookup, setLookup] = useState({ mobile: "", last_name: "" });
  const [bookings, setBookings] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [form, setForm] = useState({ id_type: "Aadhaar", id_number: "", address: "", email: "", id_photos: [] });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const doLookup = async (e) => {
    e?.preventDefault();
    if (!lookup.mobile.trim()) return toast.error("Enter your mobile number");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/checkin/${pid}/${token}/lookup`, lookup);
      if (data.bookings.length === 1) {
        setChosen(data.bookings[0]);
        setForm((f) => ({ ...f, id_type: data.bookings[0].id_type || "Aadhaar",
                                  id_number: data.bookings[0].id_number || "" }));
        setStep("form");
      } else {
        setBookings(data.bookings);
        setStep("pick");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    setBusy(false);
  };

  const uploadId = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/public/checkin/${pid}/${token}/upload-id`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, id_photos: [...f.id_photos, data.path] }));
      toast.success("ID uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  const submit = async () => {
    if (!form.id_type.trim() || !form.id_number.trim() || !form.id_photos.length) {
      return toast.error("ID type, number and at least one ID photo are mandatory");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/checkin/${pid}/${token}/self-checkin`, {
        booking_id: chosen.id,
        id_type: form.id_type.trim(), id_number: form.id_number.trim(),
        id_photos: form.id_photos, address: form.address, email: form.email,
      });
      toast.success("Checked in!"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Check-in failed"); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <BackBtn onClick={onBack} />
      <h2 className="font-bold text-slate-900">Self Check-in</h2>
      {step === "lookup" && (
        <form onSubmit={doLookup} className="mt-4 space-y-3">
          <p className="text-xs text-slate-500">We'll find your reservation using the mobile you booked with.</p>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Mobile *</label>
            <input data-testid="qr-lookup-mobile" value={lookup.mobile} onChange={(e) => setLookup({ ...lookup, mobile: e.target.value })}
              placeholder="+91 98xxxxxxxx" className={inp} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Last name (optional)</label>
            <input value={lookup.last_name} onChange={(e) => setLookup({ ...lookup, last_name: e.target.value })}
              placeholder="Verifies against your booking" className={inp} />
          </div>
          <button data-testid="qr-lookup-btn" disabled={busy} className={`${btn} bg-[#4F46E5] text-white hover:bg-[#4338CA] disabled:opacity-50`}>
            {busy ? <Loader2 className="animate-spin" size={14} /> : "Find my reservation"}
          </button>
        </form>
      )}
      {step === "pick" && (
        <div className="mt-4 space-y-2">
          <p className="text-xs text-slate-500">Multiple bookings match — pick yours.</p>
          {bookings.map((b) => (
            <button key={b.id} onClick={() => { setChosen(b); setStep("form"); }}
              className="w-full text-left border border-slate-200 rounded-lg p-3 hover:border-[#4F46E5] hover:bg-indigo-50">
              <div className="font-semibold text-sm">{b.guest} · {b.unit_name}</div>
              <div className="text-xs text-slate-500">{b.checkin} → {b.checkout}</div>
            </button>
          ))}
        </div>
      )}
      {step === "form" && chosen && (
        <div className="mt-4 space-y-3">
          <div className="bg-indigo-50 rounded-lg p-3 text-xs">
            <div className="font-semibold text-slate-800">{chosen.guest} · {chosen.unit_name}</div>
            <div className="text-slate-500 mt-0.5">{chosen.checkin} → {chosen.checkout} · {chosen.adults} adult(s), {chosen.children} child(ren)</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">ID Type *</label>
              <select data-testid="qr-id-type" value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} className={inp}>
                {["Aadhaar", "Passport", "Driver's License", "PAN", "Voter ID", "Other"].map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">ID Number *</label>
              <input data-testid="qr-id-number" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className={inp} />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Address</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">ID Photo(s) * — up to 4</label>
            <input ref={fileRef} data-testid="qr-id-file" type="file" accept="image/*,.pdf" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadId(f); }} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy || form.id_photos.length >= 4}
              className="mt-1 w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-3 text-xs font-semibold text-slate-500 hover:border-[#4F46E5] hover:text-[#4F46E5] disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />}
              {form.id_photos.length ? `${form.id_photos.length}/4 uploaded — add another` : "Upload ID photo (jpg/png/pdf)"}
            </button>
          </div>
          <button data-testid="qr-submit-checkin" onClick={submit} disabled={busy || !form.id_photos.length}
            className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50`}>
            {busy ? <Loader2 className="animate-spin" size={14} /> : <LogIn size={14} />} Complete check-in
          </button>
        </div>
      )}
    </div>
  );
}

function VisitorPassFlow({ pid, token, onBack, onDone }) {
  const [form, setForm] = useState({ visitor_name: "", visitor_mobile: "", host_room: "", host_guest: "", purpose: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.visitor_name.trim()) return toast.error("Visitor name is required");
    setBusy(true);
    try {
      await axios.post(`${API}/public/checkin/${pid}/${token}/visitor-pass`, form);
      toast.success("Visitor pass created"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
      <BackBtn onClick={onBack} />
      <h2 className="font-bold text-slate-900">Visitor Pass</h2>
      <p className="text-xs text-slate-500">Please fill in your details. The front desk will be notified.</p>
      <div>
        <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Your name *</label>
        <input data-testid="qr-visitor-name" value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} className={inp} />
      </div>
      <div>
        <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Mobile</label>
        <input value={form.visitor_mobile} onChange={(e) => setForm({ ...form, visitor_mobile: e.target.value })} className={inp} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Room / Unit</label>
          <input value={form.host_room} onChange={(e) => setForm({ ...form, host_room: e.target.value })} className={inp} />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Meeting</label>
          <input value={form.host_guest} onChange={(e) => setForm({ ...form, host_guest: e.target.value })} placeholder="Guest name" className={inp} />
        </div>
      </div>
      <div>
        <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Purpose</label>
        <input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Friend visit, delivery, etc." className={inp} />
      </div>
      <button data-testid="qr-submit-visitor" disabled={busy} className={`${btn} bg-[#4F46E5] text-white hover:bg-[#4338CA] disabled:opacity-50`}>
        {busy ? <Loader2 className="animate-spin" size={14} /> : "Submit visitor pass"}
      </button>
    </form>
  );
}
